import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountService } from '../account.service';
import { LoginRequest } from '../account.type';
import { LoginInfo } from '../../login-info/login-info.type';
import { EmailLoginField } from '../../shared/components/email-login-field/email-login-field';
import { PROXY_COUNTRIES } from '../../shared/data/proxy-countries';

@Component({
  selector: 'app-create-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDialogModule,
    MatOptionModule,
    EmailLoginField,
  ],
  templateUrl: './create-login.html',
  styleUrl: './create-login.scss'
})
export class CreateLogin {
  private _snackBar = inject(MatSnackBar);
  private _fb = inject(FormBuilder);
  private _dialogRef = inject(MatDialogRef<CreateLogin>);
  private _accountService = inject(AccountService);
  readonly preselectedType = inject<'stock' | 'customer' | null>(MAT_DIALOG_DATA, { optional: true });

  loginInfo: LoginInfo | null = null;
  loading: boolean = false;
  readonly countries = PROXY_COUNTRIES;

  loginForm: FormGroup<CreateLoginForm> = this._fb.nonNullable.group({
    name: [''],
    type: [this.preselectedType ?? '', this.preselectedType ? [] : Validators.required],
    email: ['', Validators.required],
    country: [''],
  });

  submit(): void {
    if (this.loginForm.valid) {
      this.loginForm.disable();

      const request: LoginRequest = {
        name: this.loginForm.controls['name'].value ?? '-',
        login_info_email: this.loginForm.controls['email'].value,
        type: (this.preselectedType ?? this.loginForm.controls['type'].value) as 'stock' | 'customer',
        country: this.loginForm.controls['country'].value || undefined,
      };

      this._accountService.createLogin(request).subscribe({
        next: () => {
          this._snackBar.open('Login process started', 'Close', { duration: 5000 });
          this._dialogRef.close(this.loginForm.value);
        },
        error: () => {
          this._snackBar.open('Error creating login process', 'Close', { duration: 5000 });
          this.loginForm.enable();
        },
        complete: () => {
          this.loginForm.enable();
        },
      });
    }
  }

  cancel(): void {
    this._dialogRef.close();
  }

  onLoginInfoChange(info: LoginInfo | null): void {
    this.loginInfo = info;
  }
}

interface CreateLoginForm {
  name: FormControl<string>;
  type: FormControl<string>;
  email: FormControl<string>;
  country: FormControl<string>;
}
